'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { PropsWithChildren } from 'react';

import { createPortal } from 'react-dom';

import { usePathname } from 'next/navigation';

import { bookingModalContent } from '../../_content/chrome';
import { localeFromPath } from '../../_lib/marketing-routes';

/**
 * Zentrales Buchungs-Overlay (Microsoft Bookings) – PR-4-Infrastruktur.
 *
 * Ein einziger Provider wird im Marketing-Layout gemountet. Er hört per
 * Event-Delegation auf `document` und öffnet das Overlay, sobald irgendwo im
 * Marketing-Baum ein Element mit `data-booking="messe|demo"` geklickt wird.
 * So können SERVER-Komponenten (Festival-/Demo-Band, PR-5/6) einen simplen
 * `<button data-booking="demo">` rendern, ohne selbst Client zu sein. Client-
 * Komponenten können alternativ `useBookingModal()` nutzen.
 *
 * Prompt-Injection-Sicherheit: die iframe-URL ist IMMER eine der beiden fest
 * hartkodierten Konstanten. `data-booking` wählt via Whitelist nur ZWISCHEN
 * diesen Konstanten – es gibt keinen URL-Passthrough aus DOM/User-Input.
 */

export type BookingContext = 'messe' | 'demo';

const BOOKING_URLS: Record<BookingContext, string> = {
  messe:
    'https://outlook.office.com/book/Locari1@ametis-digital.com/s/RfGc7lz4fU2ttXEt8bIy9g2',
  demo: 'https://outlook.office.com/book/Locari1@ametis-digital.com/s/3dlsk952dECScJT3leGdNQ2',
};

const VALID_CONTEXTS: readonly BookingContext[] = ['messe', 'demo'];

function isBookingContext(value: string | null): value is BookingContext {
  return (
    value !== null && (VALID_CONTEXTS as readonly string[]).includes(value)
  );
}

const TITLE_ID = 'booking-modal-title';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

interface BookingModalContextValue {
  open: (context: BookingContext) => void;
  close: () => void;
}

const BookingModalContext = createContext<BookingModalContextValue | null>(
  null,
);

export function useBookingModal(): BookingModalContextValue {
  const ctx = useContext(BookingModalContext);

  if (!ctx) {
    throw new Error(
      'useBookingModal must be used within <BookingModalProvider>',
    );
  }

  return ctx;
}

// SSR-sicheres "bin ich auf dem Client?"-Flag ohne setState-im-Effect:
// Server-Snapshot false, Client-Snapshot true. Der Portal-Render (createPortal
// nach document.body) darf erst nach dem Mount passieren.
const emptySubscribe = () => () => {};

export function BookingModalProvider({ children }: PropsWithChildren) {
  const [active, setActive] = useState<BookingContext | null>(null);

  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  const pathname = usePathname();
  const locale = localeFromPath(pathname);
  const t = bookingModalContent[locale];

  const mounted = useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );

  const open = useCallback((context: BookingContext) => {
    // Der O365-Tenant blockt iframe-Framing (X-Frame-Options) → das Overlay
    // bleibt leer. Deshalb direkt in neuem Tab öffnen (feste, hartkodierte URL).
    window.open(BOOKING_URLS[context], '_blank', 'noopener,noreferrer');
  }, []);

  const close = useCallback(() => {
    setActive(null);
  }, []);

  // Event-Delegation: fängt Klicks auf [data-booking] überall im Marketing-Baum.
  useEffect(() => {
    function onDocumentClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      const trigger = target?.closest<HTMLElement>('[data-booking]');

      if (!trigger) return;

      const value = trigger.getAttribute('data-booking');

      if (!isBookingContext(value)) return;

      event.preventDefault();
      // iframe-Framing ist tenant-seitig blockiert → direkt in neuem Tab öffnen.
      window.open(BOOKING_URLS[value], '_blank', 'noopener,noreferrer');
    }

    document.addEventListener('click', onDocumentClick);

    return () => document.removeEventListener('click', onDocumentClick);
  }, []);

  // Body-Scroll-Lock + Fokus ins Modal beim Öffnen; Cleanup gibt den Zustand
  // sauber zurück (Scroll frei + Fokus-Rückgabe an den auslösenden Trigger).
  useEffect(() => {
    if (!active) return;

    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const trigger = triggerRef.current;

    return () => {
      document.body.style.overflow = '';

      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [active]);

  // ESC + Focus-Trap (Tab/Shift+Tab zyklisch innerhalb des Modals).
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusables.length === 0) return;

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const activeEl = document.activeElement;

      if (event.shiftKey) {
        if (activeEl === first || !panel.contains(activeEl)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [close],
  );

  const contextValue = useMemo<BookingModalContextValue>(
    () => ({ open, close }),
    [open, close],
  );

  // Portal an document.body (escaped jeden Stacking-Context der Header/Banner).
  // In einen .marketing-scope-Wrapper gehüllt, damit die scoped Tokens
  // (--accent-action etc.) und Component-Styles auch außerhalb des Layout-Baums
  // greifen. Der Wrapper kollabiert auf 0-Höhe (nur position:fixed-Kinder).
  const overlay = (
    <div className="marketing-scope booking-portal">
      <div
        className={active ? 'booking-modal is-open' : 'booking-modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-hidden={active ? undefined : true}
        onKeyDown={onKeyDown}
      >
        <div className="booking-backdrop" onClick={close} aria-hidden="true" />

        <div className="booking-panel" ref={panelRef}>
          <div className="booking-panel-head">
            <h2 id={TITLE_ID} className="booking-panel-title">
              {active ? t.title[active] : ''}
            </h2>

            <button
              type="button"
              className="booking-close"
              ref={closeRef}
              onClick={close}
              aria-label={t.close}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          </div>

          {active === 'messe' ? (
            <p className="booking-notice">{t.messeNotice}</p>
          ) : null}

          <div className="booking-frame-wrap">
            <iframe
              className="booking-frame"
              src={active ? BOOKING_URLS[active] : 'about:blank'}
              title={t.iframeTitle}
            />
          </div>

          <a
            className="booking-fallback"
            href={BOOKING_URLS[active ?? 'demo']}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.fallback}
            <span aria-hidden="true"> ↗</span>
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <BookingModalContext.Provider value={contextValue}>
      {children}
      {mounted ? createPortal(overlay, document.body) : null}
    </BookingModalContext.Provider>
  );
}
